import { createInstance, type i18n as I18n, type Resource } from "i18next";
import { initReactI18next } from "react-i18next";

import type { Locale } from "./languages";

export type Dictionary = Record<string, unknown>;

/**
 * Builds an i18next instance for exactly one locale.
 *
 * The dictionary is passed in rather than imported here on purpose. On the server the
 * pre-render reads it from disk; in the browser it comes from a JSON block embedded in the
 * page. Neither path bundles every language into the client, which is the whole reason this
 * function takes an argument instead of reaching for a static import.
 *
 * A fresh instance per call: pre-rendering walks every locale in one process, and a shared
 * instance would leak the previous language into the next render.
 */
export function createI18n(locale: Locale, dictionary: Dictionary): I18n {
  const instance = createInstance();

  const resources: Resource = { [locale]: { translation: dictionary } };

  // Synchronous — the resources are already in memory, there is no backend to await.
  void instance.use(initReactI18next).init({
    lng: locale,
    // No cross-locale fallback: each page carries only its own dictionary, so a missing key
    // shows as the key. That is louder than silently English text, and the claims gate reads
    // the dictionaries directly, so a gap is caught before it ships.
    fallbackLng: false,
    resources,
    interpolation: { escapeValue: false },
  });

  return instance;
}
